import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Divider, Alert } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import { 
  useUploadDemarqueDBF, 
  useUploadDemarqueDetailsDBF, 
  useUploadBothDemarqueDBF 
} from '../../hooks/avoirHooks';

const BulkUploadDemarque = () => {
  const [demarqueFile, setDemarqueFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  
  const { handleUpload: uploadDemarque, loading: loadingDemarque, error: errorDemarque, uploadResult: resultDemarque } = useUploadDemarqueDBF();
  const { handleUpload: uploadDetails, loading: loadingDetails, error: errorDetails, uploadResult: resultDetails } = useUploadDemarqueDetailsDBF();
  const { handleUpload: uploadBoth, loading: loadingBoth, error: errorBoth, uploadResult: resultBoth } = useUploadBothDemarqueDBF();

  const handleUploadDemarque = async () => {
    if (!demarqueFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDemarque(demarqueFile);
      message.success('Demarque uploaded successfully!');
      setDemarqueFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadDetails = async () => {
    if (!detailsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDetails(detailsFile);
      message.success('DemarqueDetail uploaded successfully!');
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadBoth = async () => {
    if (!demarqueFile || !detailsFile) {
      message.error('Please select both DBF files!');
      return;
    }

    try {
      await uploadBoth(demarqueFile, detailsFile);
      message.success('Both files uploaded successfully!');
      setDemarqueFile(null);
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const demarqueProps = {
    onRemove: () => {
      setDemarqueFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDemarqueFile(file);
      return false;
    },
    fileList: demarqueFile ? [demarqueFile] : [],
    accept: '.dbf,.DBF',
  };

  const detailsProps = {
    onRemove: () => {
      setDetailsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDetailsFile(file);
      return false;
    },
    fileList: detailsFile ? [detailsFile] : [],
    accept: '.dbf,.DBF',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Upload Demarque DBF Files" style={{ marginBottom: 24 }}>
        <Alert
          message="DBF File Upload Instructions"
          description={
            <div>
              <p><strong>Upload Demarque Header file (.dbf):</strong> Contains the main demarque records (typically DEMARQUE.dbf)</p>
              <p><strong>Upload Demarque Detail file (.dbf):</strong> Contains the detail/line items for demarques (typically DETDEMARQUE.dbf)</p>
              <p><strong>Upload Both:</strong> Upload both files together (recommended)</p>
              <p style={{ marginTop: 8, color: '#52c41a' }}>
                <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Upload DEMARQUE.dbf */}
          <Card size="small" title="1. Upload Demarque Headers (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...demarqueProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDemarque || loadingBoth}>
                  Select DBF File (Headers)
                </Button>
              </Upload>
              {demarqueFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {demarqueFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDemarque}
                disabled={!demarqueFile || loadingDemarque || loadingBoth}
                loading={loadingDemarque}
              >
                {loadingDemarque ? 'Uploading...' : 'Upload Demarque'}
              </Button>
              {errorDemarque && (
                <Alert message={errorDemarque} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDemarque && (
                <Alert
                  message="Upload Successful"
                  description={resultDemarque.message || 'Demarque seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          {/* Upload DETDEMARQUE.dbf */}
          <Card size="small" title="2. Upload Demarque Details (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...detailsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDetails || loadingBoth}>
                  Select DBF File (Details)
                </Button>
              </Upload>
              {detailsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {detailsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDetails}
                disabled={!detailsFile || loadingDetails || loadingBoth}
                loading={loadingDetails}
              >
                {loadingDetails ? 'Uploading...' : 'Upload Details'}
              </Button>
              {errorDetails && (
                <Alert message={errorDetails} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDetails && (
                <Alert
                  message="Upload Successful"
                  description={resultDetails.message || 'DemarqueDetail seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          <Divider>OR</Divider>

          {/* Upload Both */}
          <Card size="small" title="3. Upload Both Files Together (Recommended)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Upload {...demarqueProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Headers File (.dbf)
                  </Button>
                </Upload>
                {demarqueFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {demarqueFile.name}
                  </span>
                )}
              </div>
              <div>
                <Upload {...detailsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Details File (.dbf)
                  </Button>
                </Upload>
                {detailsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {detailsFile.name}
                  </span>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={handleUploadBoth}
                disabled={!demarqueFile || !detailsFile || loadingBoth}
                loading={loadingBoth}
                style={{ marginTop: 16 }}
              >
                {loadingBoth ? 'Uploading Both Files...' : 'Upload Both Files'}
              </Button>
              {errorBoth && (
                <Alert message={errorBoth} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBoth && (
                <Alert
                  message="Upload Successful"
                  description={resultBoth.message || 'Both files seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default BulkUploadDemarque;

